import React, { useEffect, useState } from 'react';
import { WithStyles } from '@material-ui/styles';
import Grid from '@material-ui/core/Grid';
import Spinner from '@material-ui/core/CircularProgress';
import {
  Divider, MenuItem, Select, Switch,
} from '@material-ui/core';
import styles from './styles';
import { ActionPayload } from '../../types/main';
import CustomButton from '../CustomButton';
import BatteryIcon from '../Icons/BatteryIcon';
import {
  refreshSmartLockDetailsRequest,
  smartLockDetailsRequest
} from '../../redux/actions/smartLocks';
import { ValidationErrors } from '../../utils/helpers';
import { SmartLock, SmartLockCode, SmartLockScheduledCode } from '../../redux/types/smartLocks';
import {
  PropertyAccessSettings,
  PropertyDetailsInfo,
} from '../../redux/types/properties';
import SmartLockCodesForm from '../SmartLockCodesForm';
import SmartLockScheduledCodesForm from '../SmartLockScheduledCodesForm';
import { usePermissions } from '../../hooks/usePermissions';

type Props = {
  loadSmartLockDetails: (
    smartLockSerial: ActionPayload<typeof smartLockDetailsRequest>
  ) => void;
  refreshSmartLockDetails: (
    smartLockSerial: ActionPayload<typeof refreshSmartLockDetailsRequest>
  ) => void;
  handleAccessSettings: (settings: PropertyAccessSettings) => void;
  handleSave: () => void;
  property: PropertyDetailsInfo;
  loading: boolean;
  refreshing: boolean;
  smartLockSerial: string;
  smartLockDetails: SmartLock | null;
} & WithStyles<typeof styles>;

const relockIntervalOptions = ['15 min', '20 min', '25 min', '30 min'];
const lockupOptions = ['8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM', '10:00 PM', '10:30 PM', '11:00 PM'];

const SmartLockPanel: React.FC<Props> = (props: Props) => {
  const {
    classes,
    loading,
    refreshing,
    loadSmartLockDetails,
    refreshSmartLockDetails,
    smartLockSerial,
    smartLockDetails,
    property,
    handleAccessSettings,
    handleSave
  } = props;

  const {
    access_settings: accessSettings,
    end_of_day_lockup: endOfDayLockup,
    relock_interval: interval
  } = property;

  const [isOpenSidebar, setIsOpenSidebar] = useState(false);
  const [accessCodes, setAccessCodes] = useState<SmartLockCode[]>([]);
  const [newCode, setNewCode] = useState<SmartLockCode | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [validationErrors, setValidationErrors] = useState<
    ValidationErrors<SmartLockCode>
  >({});
  const [checked, setChecked] = useState(Boolean(accessSettings) ?? false);
  const [relockInterval, setRelockInterval] = useState<string | null>(
    interval ?? null,
  );
  const [lockup, setLockup] = useState<string | null>(endOfDayLockup ?? null);
  const batteryLevel = (
    smartLockDetails?.battery_level && smartLockDetails?.battery_level * 100
  )?.toFixed();

  const [isEditingScheduledCodes, setIsEditingScheduledCodes] = useState(false);
  const [scheduledCodes, setScheduleCodes] = useState<SmartLockScheduledCode[]>([]);

  const { canEdit } = usePermissions();

  useEffect(() => {
    if (!checked) {
      setRelockInterval(null);
      setLockup(null);

      return;
    }

    setRelockInterval(interval ?? '15 min');
    setLockup(endOfDayLockup ?? '10:00 PM');
  }, [checked, endOfDayLockup, interval]);

  useEffect(() => {
    const settings = {
      propertyId: property.property_id,
      accessSettings: checked,
      relockInterval,
      endOfDayLockup: lockup,
    };

    handleAccessSettings(settings);
  }, [
    checked,
    lockup,
    relockInterval,
    handleAccessSettings,
    property.property_id,
  ]);

  useEffect(() => {
    if (!smartLockDetails?.accessCodes?.length) {
      return;
    }

    const filteredCodes = smartLockDetails?.accessCodes?.filter(
      (lockCode: SmartLockCode) => lockCode?.status !== 'removing'
    );

    setAccessCodes(filteredCodes);
  }, [smartLockDetails]);

  useEffect(() => {
    if (!smartLockDetails?.scheduledCodes) {
      return;
    }
    setScheduleCodes(smartLockDetails?.scheduledCodes);
  }, [smartLockDetails]);

  useEffect(() => {
    loadSmartLockDetails(smartLockSerial);
  }, [loadSmartLockDetails, smartLockSerial]);

  useEffect(() => {
    if (!isOpenSidebar || isEditingScheduledCodes) {
      return;
    }

    const timer = setTimeout(() => {
      if (seconds < 60) {
        setSeconds(prevState => prevState + 10);
      }

      if (!refreshing) {
        refreshSmartLockDetails(smartLockSerial);
      }
    }, 10000);

    if (seconds >= 60) {
      clearTimeout(timer);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timer);
    };
  }, [
    refreshSmartLockDetails,
    refreshing,
    seconds,
    isOpenSidebar,
    smartLockSerial,
    isEditingScheduledCodes,
  ]);

  return (
    <>
      <Grid container alignItems="center" spacing={4}>
        <Grid item>
          <Switch
            checked={checked}
            onChange={() => {
              setChecked(!checked);
            }}
            disabled={!canEdit}
          />
        </Grid>

        <Grid item>
          <h2 className={`${classes.title} ${classes.primaryTitle}`}>
            Staffed Model Access Settings
          </h2>
        </Grid>
        <Grid item>
          <p
            className={`${classes.switchText} ${!checked && classes.disabled}`}
          >
            {checked ? 'Enabled' : 'Disabled'}
          </p>
        </Grid>
      </Grid>

      <Divider style={{ marginBottom: '20px' }} />

      <div className={`${!checked && classes.disabled}`}>
        <Grid container alignItems="center">
          <Grid item>
            <p className={`${classes.title} ${classes.secondaryTitle}`}>
              Relock Interval
            </p>
          </Grid>
          <Grid item>
            <Select
              disabled={!checked || !canEdit}
              className={classes.select}
              variant="outlined"
              value={relockInterval}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                const { value } = event.target;
                setRelockInterval(value as string);
              }}
            >
              {relockIntervalOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        <Grid container alignItems="center">
          <Grid item>
            <p className={`${classes.title} ${classes.secondaryTitle}`}>
              End Of Day Lockup
            </p>
          </Grid>
          <Grid item>
            <Select
              disabled={!checked || !canEdit}
              className={classes.select}
              variant="outlined"
              value={lockup}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                const { value } = event.target;
                setLockup(value as string);
              }}
            >
              {lockupOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </div>

      <CustomButton
        onClick={handleSave}
        variant="orange"
        className={classes.btnStyle}
        disabled={!canEdit}
      >
        Save
      </CustomButton>
      <Divider style={{ marginTop: '20px' }} />

      <Grid item xs={12} sm={12}>
        {loading && !isOpenSidebar && (
          <div className={classes.progressWrapper}>
            <Spinner className={classes.progress} />
          </div>
        )}
        {!loading && !smartLockDetails && (
          <div className={classes.statusTitle}>No smart lock data</div>
        )}
        {smartLockDetails && (
          <>
            <p className={classes.statusTitle}>Status/Activity</p>
            <Grid container alignItems="center" className={classes.deviceTitle}>
              <span className={classes.deviceName}>Smart Lock</span>
            </Grid>
            <Grid
              container
              className={classes.card}
              direction="row"
            >
              <Grid
                onClick={() => {
                  setIsOpenSidebar(!isOpenSidebar);
                }}
                item
                xs={12}
                className={classes.cardHeader}
                container
              >
                <Grid item xs className={classes.cardTitle}>
                  <span>{smartLockDetails?.name}</span>
                </Grid>
                <Grid item xs className={classes.cardStatus}>
                  <Grid container justify="space-between" alignItems="center">
                    <Grid item xs>
                    <span>
                      {smartLockDetails?.locked ? 'Locked' : 'Unlocked'}
                    </span>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container item xs justify="flex-end">
                  <Grid item className={classes.batteryCounter}>
                    {batteryLevel}
                    %
                  </Grid>
                  <Grid className={classes.iconWrapper}>
                    <BatteryIcon className={classes.batteryIcon} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                className={`${classes.expandableContent} ${
                  isOpenSidebar ? classes.expanded : classes.collapsed
                }`}
                xs={12}
              >
                <Grid container spacing={4} className={classes.detailsContainer}>
                  <SmartLockCodesForm
                    smartLockSerial={smartLockSerial}
                    accessCodes={accessCodes}
                    newCode={newCode}
                    setNewCode={setNewCode}
                    setSeconds={setSeconds}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                  />

                  <SmartLockScheduledCodesForm
                    smartLockSerial={smartLockSerial}
                    onEditingStateChange={setIsEditingScheduledCodes}
                    builderId={property.builder_id}
                    propertyId={property.property_id}
                    scheduledCodes={scheduledCodes}
                  />
                </Grid>
              </Grid>

            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default SmartLockPanel;
